import React, { useEffect, useRef } from "react";
import FeaturesSection from "../components/fecturesection";
import AboutSection from "../components/AboutSection";
import CtaSection from "../components/CtaSection";
import TextType from "../components/TextType";
import ScrollStack, { ScrollStackItem } from "../components/ScrollStack";
import MagicBento from "../components/MagicBento";
import PillNav from "../components/PillNav";
import FlowingMenu from "../components/FlowingMenu";
import AnimatedContent from '../components/AnimatedContent';
import "../css/landingpage.css";
import Gojo3DScene from "../components/main";

const HeroSection = () => {
  const textRef = useRef(null);
  
  useEffect(() => {
    const textElement = textRef.current;
    textElement.style.opacity = 0;
    textElement.style.transform = "translateY(20px)";
    textElement.style.transition = "opacity 0.8s ease, transform 0.8s ease";

    setTimeout(() => {
      textElement.style.opacity = 1;
      textElement.style.transform = "translateY(0)";
    }, 100);
  }, []);

  return (
    <section id="home" className="hero-section">
      <div className="hero-content">
        <h2 className="greeting-text">Hello! I'm</h2>
        <h1 ref={textRef} className="main-heading">
          Hassan
        </h1>
        <h1 ref={textRef} className="main-heading">
          khan 
        </h1>
        
        <p className="hero-subtext">
          <TextType 
            text="Turning coffee into code (sometimes bugs too, but that’s a feature)."
            speed={50}
          />
        </p>
        
        <div className="hero-cta">
          <button 
            className="primary-btn"
            onClick={() => {
              const projectsSection = document.getElementById('projects');
              if (projectsSection) {
                projectsSection.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start'
                });
              }
            }}
          >
            View Projects
          </button>
          <button className="secondary-btn">Get in Touch</button>
        </div>
      </div>
    </section>
  );
};

const LandingPage = () => {
  useEffect(() => {
    // Clear any leftover full-screen state from Mainp transition (mobile fix)
    document.body.classList.remove('full-screen');

    // Initialize Lenis smooth scrolling
    const initLenis = async () => {
      const lenis = new (await import('lenis')).default({
        duration: 1.2,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        direction: 'vertical',
        gestureDirection: 'vertical',
        smooth: true,
        mouseMultiplier: 1,
        smoothTouch: false,
        touchMultiplier: 2,
        infinite: false,
      });

      function raf(time) {
        lenis.raf(time);
        requestAnimationFrame(raf);
      }

      requestAnimationFrame(raf);

      return lenis;
    };

    let lenisInstance;
    initLenis().then(lenis => {
      lenisInstance = lenis;
    });

    // Initialize cursor spotlight effect
    const initCursorSpotlight = () => {
      const spotlight = document.createElement("div");
      spotlight.className = "cursor-spotlight";
      spotlight.style.cssText = `
        position: fixed;
        width: 600px;
        height: 600px;
        border-radius: 50%;
        pointer-events: none;
        background: radial-gradient(circle,
          rgba(132, 0, 255, 0.15) 0%,
          rgba(132, 0, 255, 0.08) 15%,
          rgba(132, 0, 255, 0.04) 25%,
          rgba(132, 0, 255, 0.02) 40%,
          rgba(132, 0, 255, 0.01) 65%,
          transparent 70%
        );
        z-index: 1000;
        opacity: 0;
        transform: translate(-50%, -50%);
        mix-blend-mode: screen;
        transition: opacity 0.3s ease;
      `;
      document.body.appendChild(spotlight);

      const handleMouseMove = (e) => {
        spotlight.style.left = e.clientX + 'px';
        spotlight.style.top = e.clientY + 'px';
        spotlight.style.opacity = '1';
      };

      const handleMouseLeave = () => {
        spotlight.style.opacity = '0';
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseleave', handleMouseLeave);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseleave', handleMouseLeave);
        if (spotlight.parentNode) {
          spotlight.parentNode.removeChild(spotlight);
        }
      };
    };

    const cleanupSpotlight = initCursorSpotlight();

    return () => {
      if (lenisInstance) {
        lenisInstance.destroy();
      }
      if (typeof cleanupSpotlight === 'function') {
        cleanupSpotlight();
      }
    };
  }, []);

  return (
    <div className="landing-page">
      <div className="corner-light-left"></div>
      <div className="corner-light-right"></div>
      <div className="top-email">
        <a href="mailto:hk.devguy@gmail.com" className="top-email-link">
          <img src="/logo.png" alt="Logo" className="top-email-logo" />
          hk.devguy@gmail.com
        </a>
      </div>
      <PillNav
        logo="/logo.png"
        logoAlt="Hassan Khan"
        items={[
          { label: 'Home', href: '/' },
          { label: 'About', href: '#about' },
          { label: 'Projects', href: '#projects' },
          { label: 'Contact', href: '#contact' }
        ]}
        activeHref="/"
        className="custom-nav"
        ease="power2.easeOut"
        baseColor="#000000"
        pillColor="#ffffff"
        hoveredPillTextColor="#ffffff"
        pillTextColor="#000000"
      />
      <HeroSection />
      <Gojo3DScene />
      <FeaturesSection />
      <section id="about">
        <AboutSection />
      </section>
      
      {/* Projects Section */}
      <section id="projects" className="scroll-stack-section">
        <div className="section-container">
          <AnimatedContent
            distance={150}
            direction="vertical"
            reverse={false}
            duration={1.2}
            ease="bounce.out"
            initialOpacity={0.2}
            animateOpacity
            scale={1.1}
            threshold={0.2}
            delay={0.3}
          >
            <h2 className="section-heading">My Work</h2>
          </AnimatedContent>
          <ScrollStack
            itemDistance={120}
            itemScale={0.05}
            itemStackDistance={40}
            stackPosition="25%"
            scaleEndPosition="15%"
            baseScale={0.8}
            rotationAmount={2}
            blurAmount={1}
            onStackComplete={() => console.log('ScrollStack animation completed!')}
          >
            <ScrollStackItem>
              <h3>Pro Go</h3>
              <p>A full-stack app for service provider</p>
              <div className="project-tech">
                <span>Flutter</span>
                <span>Firebase</span>
                <span>Dart</span>
              </div>
              <a className="scroll-card-cta" href="/portfolio/project/pro-go">Click here →</a>
            </ScrollStackItem>
            
            <ScrollStackItem>
              <h3>Voting Management System</h3>
              <p>A full-stack web portal for voting management</p>
              <div className="project-tech">
                <span>React</span>
                <span>Node.js</span>
                <span>Express</span>
                <span>Postgresql</span>
              </div>
              <a className="scroll-card-cta" href="/portfolio/project/voting-management-system">Click here →</a>
            </ScrollStackItem>
            
            <ScrollStackItem>
              <h3>Foody-Fast</h3>
              <p>Foody-Fast app for my university friends</p>
              <div className="project-tech">
                <span>Flutter</span>
                <span>Firebase</span>
                <span>Stripe</span>
              </div>
              <a className="scroll-card-cta" href="/portfolio/project/foody-fast">Click here →</a>
            </ScrollStackItem>
            
           {/*  {<ScrollStackItem>
              <h3>Ping Pong</h3>
              <p>Ping pong game using hand gestures</p>
              <div className="project-tech">
                <span>Python</span>
                <span>Opencv</span>
              </div>
              <a className="scroll-card-cta" href="/portfolio/project/ping-pong">Click here →</a>
            </ScrollStackItem>} */}
          </ScrollStack>
        </div>
      </section>
      
      {/* MagicBento Section */}
      <section className="magic-bento-section">
        <div className="section-container">
          <AnimatedContent
            distance={150}
            direction="vertical"
            reverse={false}
            duration={1.2}
            ease="bounce.out"
            initialOpacity={0.2}
            animateOpacity
            scale={1.1}
            threshold={0.2}
            delay={0.3}
          >
            <h2 className="section-heading">Technical Skills</h2>
          </AnimatedContent>
          <MagicBento 
            textAutoHide={true}
            enableStars={true}
            enableSpotlight={true}
            enableBorderGlow={true}
            enableTilt={true}
            enableMagnetism={true}
            clickEffect={true}
            spotlightRadius={300}
            particleCount={12}
            glowColor="83, 52, 131"
          />
        </div>
      </section>
      
      <CtaSection />
      
      {/* Contact Section - FlowingMenu Footer */}
      <section id="contact" className="flowing-menu-footer">
        <div className="footer-container">
          <FlowingMenu 
            items={[
              { link: 'https://www.linkedin.com/in/hassan-khan-7b9b66283?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=ios_app', text: 'Linkedin', image: '/logo.png' },
              { link: 'https://github.com/hassan689', text: 'Github', image: '/logo.png' },
              { link: 'https://www.instagram.com/hassankhan_787/', text: 'Instagram', image: '/logo.png' },
              { link: 'https://www.threads.com/@hassankhan_787?xmt=AQF0oRRpadeTRH0xFgz-qHOZpw3osWg1-ijalvLBzwz4BHk', text: 'Threads', image: '/logo.png' }
            ]} 
          />
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
